/**
 * WorkerClockInReviews — the worker's OWN face enrollment + clock-in
 * face-review items and their outcome (read-only; adjudication is admin-side
 * on Face Templates / Time Entries).
 *
 * Closes the deep-link loop: the clock-in face-review DECISION notification
 * links to /me/clock-in-reviews — previously a dead route on web (bounced to
 * the dashboard). Mirror of the mobile clock-in-reviews screen.
 * Backed by GET /v1/me/clock-in-reviews (MeFaceReviewItem[]).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError, meApi } from '../../api';
import WorkerListToolbar, { matchesSubmittedFilter } from '../../components/worker/WorkerListToolbar';
import { activeLocale } from '../../utils/datetime';
import { translateApiError } from '../../i18n/translateApiError';

interface FaceReviewItem {
  id: string;
  type: string; // face_enrollment | clock_in_face_review
  status: string; // pending | approved | rejected | superseded
  submitted_at?: string | null;
  reviewed_at?: string | null;
  data?: Record<string, unknown> | null;
}

const STATUS_COLOR: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  superseded: 'default',
};

function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(activeLocale());
}

const WorkerClockInReviews: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<FaceReviewItem[]>([]);
  // Owner audit 2026-09-04: face reviews 也没有状态和其他的过滤. It was the one list surface in
  // the portal with no control at all — and it is the one a worker opens precisely to find
  // whether a specific check was approved. The fourth real status, `superseded`, is deliberately
  // not an option: it means a newer enrolment replaced this one, which nobody filters FOR. It
  // stays visible under All rather than being hidden.
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await meApi.clockInReviews.list()) as FaceReviewItem[];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('worker:clockInReviews.loadFailed'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const kindLabel = (ty: string) =>
    ty === 'face_enrollment'
      ? t('worker:clockInReviews.kindEnrollment')
      : t('worker:clockInReviews.kindReview');
  const visibleRows = useMemo(
    // GROUPED, not exact-match — see the same note in WorkerAttendanceRequests. `pending` belongs under
    // `in_progress` and `approved` under `completed`, so equality would leave two chips dead.
    () => rows.filter((r) => matchesSubmittedFilter(r.status, statusFilter)),
    [rows, statusFilter],
  );

  const statusLabel = (s: string) =>
    t(`worker:clockInReviews.status.${s}`, { defaultValue: s });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>
        {t('worker:clockInReviews.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('worker:clockInReviews.subtitle')}
      </Typography>

      <WorkerListToolbar
        set="submitted"
        value={statusFilter}
        onChange={(v) => setStatusFilter(v as typeof statusFilter)}
      />

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Paper elevation={0} sx={{ width: '100%', p: 4, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Paper>
      ) : visibleRows.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {rows.length === 0 ? t('worker:clockInReviews.empty') : t('worker:clockInReviews.noMatch')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {rows.length === 0 ? t('worker:clockInReviews.emptyHint') : t('worker:clockInReviews.noMatchHint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('worker:clockInReviews.colKind')}</TableCell>
                <TableCell>{t('worker:clockInReviews.colSubmitted')}</TableCell>
                <TableCell>{t('worker:clockInReviews.colReviewed')}</TableCell>
                <TableCell>{t('worker:clockInReviews.colStatus')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{kindLabel(r.type)}</TableCell>
                  <TableCell>{fmt(r.submitted_at)}</TableCell>
                  <TableCell>{fmt(r.reviewed_at)}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={STATUS_COLOR[r.status] ?? 'default'}
                      label={statusLabel(r.status)}
                      sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default WorkerClockInReviews;
